import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, ChevronDown, Download, Filter, Loader2, Receipt as ReceiptIcon, Search, Wallet, X } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "../../components/layout/AdminShell";
import EmptyState from "../../components/EmptyState";
import ReceiptDetailDrawer from "../../components/ReceiptDetailDrawer";
import StatusBadge, { ConfidenceMeter, PayoutBadge } from "../../components/StatusBadge";
import { useAuth } from "../../context/AuthContext";
import { ReceiptsApi, ReportsApi, TeamApi, errorMessage } from "../../lib/api";
import { categoryLabel, downloadBlob, formatDate, formatMoney } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Input } from "../../components/ui/input";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { useI18n } from "../../i18n";

const STATUS_FILTERS = [
  { key: "all", labelKey: "common.all" },
  { key: "needs_review", labelKey: "status.needs_review" },
  { key: "parsed", labelKey: "status.parsed" },
  { key: "approved", labelKey: "status.approved" },
  { key: "queued,processing", labelKey: "status.processing" },
  { key: "failed", labelKey: "status.failed" },
  { key: "rejected", labelKey: "status.rejected" },
];

const Receipts = () => {
  const { t } = useI18n();
  const { currency, categories, isAdmin, isAccountingFirm } = useAuth();
  const [filters, setFilters] = useState({
    status: "all",
    category: "all",
    user_id: "all",
    q: "",
    date_from: "",
    date_to: "",
  });
  const [data, setData] = useState({ receipts: [], total: 0 });
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const query = useMemo(() => {
    const p = { limit: 200 };
    if (filters.status !== "all") p.status = filters.status;
    if (filters.category !== "all") p.category = filters.category;
    if (filters.user_id !== "all") p.user_id = filters.user_id;
    if (filters.q.trim()) p.q = filters.q.trim();
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ReceiptsApi.list(query);
      setData(res);
    } catch (e) {
      toast.error(errorMessage(e, "Could not load receipts"));
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  useEffect(() => {
    TeamApi.members()
      .then((d) => setMembers(d.members || []))
      .catch(() => setMembers([]));
  }, []);

  // deep link from the notification centre: /app/receipts?receipt=<id>
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const focus = searchParams.get("receipt");
    if (focus) {
      setOpenId(focus);
      const next = new URLSearchParams(searchParams);
      next.delete("receipt");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const toggleAll = (checked) =>
    setSelected(checked ? data.receipts.filter((r) => r.gross_total != null).map((r) => r.id) : []);

  const bulkPayout = async () => {
    setBusy(true);
    try {
      const res = await ReceiptsApi.bulkMarkPaid(selected);
      toast.success(t("receipts.paidBulkToast", {
        count: res.paid,
        amount: formatMoney(res.amount, currency),
      }));
      setSelected([]);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Could not mark refunds as paid"));
    } finally {
      setBusy(false);
    }
  };

  const bulkApprove = async () => {
    setBusy(true);
    try {
      const res = await ReceiptsApi.bulkApprove(selected);
      toast.success(`${res.approved} receipt(s) approved`);
      setSelected([]);
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Bulk approve failed"));
    } finally {
      setBusy(false);
    }
  };

  const [exporting, setExporting] = useState(null);

  // same generators as the Reports page: universal CSV, Minimax CSV, Synesis eURA.txt
  const exportPreset = async (preset) => {
    setExporting(preset);
    try {
      const { blob, disposition, documentNo, rowCount } = await ReportsApi.download(
        { ...query, preset },
        "csv"
      );
      const match = /filename="?([^";]+)"?/.exec(disposition || "");
      downloadBlob(blob, match?.[1] || `receipts-${preset}.csv`);
      if (rowCount === 0) {
        toast.info(t("reports.emptyExport"));
      } else {
        toast.success(t("reports.downloadedCsv"), {
          description: documentNo ? `${t("reports.docNumber")} ${documentNo}` : undefined,
        });
      }
    } catch (e) {
      toast.error(errorMessage(e, "Export failed"));
    } finally {
      setExporting(null);
    }
  };

  const hasFilters =
    filters.status !== "all" ||
    filters.category !== "all" ||
    filters.user_id !== "all" ||
    filters.q ||
    filters.date_from ||
    filters.date_to;

  return (
    <AdminShell
      title={t("receipts.title")}
      subtitle={t("receipts.subCount", { count: data.total })}
      actions={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid="receipts-export-button">
              {exporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {t("receipts.exportMenu")}
              <ChevronDown className="ml-1.5 h-3.5 w-3.5 opacity-70" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <DropdownMenuLabel>{t("receipts.exportLayout")}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => exportPreset("universal")}
              data-testid="receipts-export-universal"
            >
              {t("reports.presetUniversal")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => exportPreset("minimax")}
              data-testid="receipts-export-minimax"
            >
              {t("reports.presetMinimax")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => exportPreset("synesis")}
              data-testid="receipts-export-synesis"
            >
              {t("reports.presetSynesis")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      }
    >
      <div className="ll-card sticky top-[64px] z-10 p-3" data-testid="receipts-filter-bar">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative min-w-[200px] flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder={t("receipts.searchPlaceholder")}
              value={filters.q}
              onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
              data-testid="receipts-search-input"
            />
          </div>
          <Select
            value={filters.category}
            onValueChange={(v) => setFilters((p) => ({ ...p, category: v }))}
          >
            <SelectTrigger className="w-[170px]" data-testid="receipts-category-filter">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("receipts.allCategories")}</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.key} value={c.key}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={filters.user_id}
            onValueChange={(v) => setFilters((p) => ({ ...p, user_id: v }))}
          >
            <SelectTrigger className="w-[170px]" data-testid="receipts-employee-filter">
              <SelectValue placeholder="Employee" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("receipts.everyone")}</SelectItem>
              {members.map((m) => (
                <SelectItem key={m.id} value={m.id}>
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            type="date"
            className="w-[150px]"
            value={filters.date_from}
            onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value }))}
            data-testid="receipts-date-from"
          />
          <Input
            type="date"
            className="w-[150px]"
            value={filters.date_to}
            onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value }))}
            data-testid="receipts-date-to"
          />
          {hasFilters ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                setFilters({ status: "all", category: "all", user_id: "all", q: "", date_from: "", date_to: "" })
              }
              data-testid="receipts-clear-filters"
            >
              <X className="mr-1 h-3.5 w-3.5" /> {t("common.clear")}
            </Button>
          ) : null}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          <Filter className="mr-1 h-3.5 w-3.5 text-muted-foreground" />
          {STATUS_FILTERS.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setFilters((p) => ({ ...p, status: s.key }))}
              data-testid={`receipts-status-chip-${s.key.replace(/,/g, "-")}`}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                filters.status === s.key
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:bg-accent"
              }`}
            >
              {t(s.labelKey)}
            </button>
          ))}
        </div>
      </div>

      {selected.length ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 p-3">
          <span className="text-sm font-medium">{t("receipts.selected", { count: selected.length })}</span>
          <div className="flex gap-2">
            <Button size="sm" onClick={bulkApprove} disabled={busy} data-testid="bulk-approve-button">
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              {t("receipts.approveSelected")}
            </Button>
            {!isAccountingFirm ? (
            <Button
              size="sm"
              variant="secondary"
              onClick={bulkPayout}
              disabled={busy}
              data-testid="bulk-payout-button"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wallet className="mr-2 h-4 w-4" />}
              {t("receipts.markPaid")}
            </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={() => setSelected([])}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      ) : null}

      <div className="mt-4">
        {loading && !data.receipts.length ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14 rounded-xl" />
            ))}
          </div>
        ) : data.receipts.length === 0 ? (
          <EmptyState
            icon={ReceiptIcon}
            title={hasFilters ? t("receipts.emptyFiltered") : t("receipts.emptyTitle")}
            description={hasFilters ? t("receipts.emptyBodyFiltered") : t("receipts.emptyBody")}
            actionLabel={hasFilters ? t("receipts.clearFilters") : t("receipts.scanCta")}
            onAction={() =>
              hasFilters
                ? setFilters({ status: "all", category: "all", user_id: "all", q: "", date_from: "", date_to: "" })
                : (window.location.href = "/app/scan")
            }
          />
        ) : (
          <div className="ll-card ll-scroll overflow-x-auto">
            <Table data-testid="receipts-table">
              <TableHeader className="bg-muted/40">
                <TableRow>
                  <TableHead className="w-[40px]">
                    <Checkbox
                      checked={selected.length > 0 && selected.length === data.receipts.filter((r) => r.gross_total != null).length}
                      onCheckedChange={toggleAll}
                      aria-label="Select all"
                      data-testid="receipts-select-all"
                    />
                  </TableHead>
                  <TableHead>{t("common.vendor")}</TableHead>
                  <TableHead>{t("common.employee")}</TableHead>
                  <TableHead>{t("common.date")}</TableHead>
                  <TableHead>{t("common.receiptNo")}</TableHead>
                  <TableHead>{t("common.category")}</TableHead>
                  <TableHead className="text-right">{t("common.net")}</TableHead>
                  <TableHead className="text-right">{t("common.vat")}</TableHead>
                  <TableHead className="text-right">{t("common.gross")}</TableHead>
                  <TableHead className="text-right">{t("common.refund")}</TableHead>
                  <TableHead>{t("common.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.receipts.map((r) => (
                  <TableRow
                    key={r.id}
                    className="cursor-pointer hover:bg-muted/50"
                    data-testid="receipt-row"
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.includes(r.id)}
                        disabled={r.gross_total == null}
                        onCheckedChange={(v) =>
                          setSelected((prev) => (v ? [...prev, r.id] : prev.filter((x) => x !== r.id)))
                        }
                        aria-label={`Select ${r.vendor_name || "receipt"}`}
                      />
                    </TableCell>
                    <TableCell className="max-w-[200px]" onClick={() => setOpenId(r.id)}>
                      <div className="truncate font-medium">{r.vendor_name || r.filename || "Receipt"}</div>
                      {r.vendor_tax_id ? (
                        <div className="truncate font-mono text-xs text-muted-foreground">{r.vendor_tax_id}</div>
                      ) : null}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm" onClick={() => setOpenId(r.id)}>
                      {r.user_name}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm" onClick={() => setOpenId(r.id)}>
                      {r.issue_date ? formatDate(r.issue_date) : "—"}
                    </TableCell>
                    <TableCell className="font-mono text-xs" onClick={() => setOpenId(r.id)}>
                      {r.receipt_number || "—"}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm" onClick={() => setOpenId(r.id)}>
                      {r.category ? categoryLabel(r.category, categories) : "—"}
                    </TableCell>
                    <TableCell className="ll-num text-right" onClick={() => setOpenId(r.id)}>
                      {r.net_total != null ? formatMoney(r.net_total, r.currency || currency) : "—"}
                    </TableCell>
                    <TableCell className="ll-num text-right" onClick={() => setOpenId(r.id)}>
                      {r.vat_total != null ? formatMoney(r.vat_total, r.currency || currency) : "—"}
                    </TableCell>
                    <TableCell
                      className="ll-num text-right font-semibold"
                      onClick={() => setOpenId(r.id)}
                      data-testid="receipt-gross-amount"
                    >
                      {r.gross_total != null ? formatMoney(r.gross_total, r.currency || currency) : "—"}
                    </TableCell>
                    <TableCell className="ll-num text-right text-primary" onClick={() => setOpenId(r.id)}>
                      {r.refund?.amount ? formatMoney(r.refund.amount, r.currency || currency) : "—"}
                      {r.refund?.amount && !isAccountingFirm ? (
                        <span className="mt-1 block">
                          <PayoutBadge status={r.payout_status} />
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell onClick={() => setOpenId(r.id)}>
                      <div className="flex flex-col gap-1">
                        <StatusBadge status={r.status} />
                        {r.confidence != null ? <ConfidenceMeter value={r.confidence} /> : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      <ReceiptDetailDrawer
        receiptId={openId}
        open={Boolean(openId)}
        onClose={() => setOpenId(null)}
        onChanged={load}
        isAdmin={isAdmin}
        categories={categories}
        currency={currency}
      />
    </AdminShell>
  );
};

export default Receipts;
